import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import * as Yup from "yup";
import {
  DATE_FORMAT,
  INPUT_LIMIT,
  REGEX_ISO_DATE_FORMAT,
} from "../../../helpers/constants";
import { KeyValueMap } from "../../../redux/slices/appState/models";
import { CreateMessageKey } from "./models";

// Extend dayjs with the custom parse format plugin
dayjs.extend(customParseFormat);
dayjs.extend(timezone);
dayjs.extend(utc);

// Helper function to parse date strings with multiple format support
const parseDateAndTime = (
  dateString: string,
  userCountryDateFormatWithTime: string,
  timeZone?: string,
) => {
  // Try different formats
  const formats = [userCountryDateFormatWithTime];

  // First try with specific formats
  for (const format of formats) {
    const parsed = dayjs(dateString, format, true);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  //  Handle GMT / ISO strings
  const looksLikeGmtOrIso =
    dateString.includes("GMT") ||
    dateString.includes("UTC") ||
    REGEX_ISO_DATE_FORMAT.test(dateString);

  if (looksLikeGmtOrIso) {
    // Interpret as UTC first
    const utcDate = dayjs.utc(dateString);
    if (!utcDate.isValid()) return null;

    // Then convert to target timezone if provided
    return timeZone ? utcDate.tz(timeZone) : utcDate;
  }

  return null;
};

const isDateAlreadyConvertedWithTimezone = (dateString: string) => {
  return dateString.includes("/");
};

export const CreateSystemMessageSchema = (
  commonValidationMessages: KeyValueMap,
  userCountryDateFormatWithTime: string,
) =>
  Yup.object().shape({
    [CreateMessageKey.countryAndLanguage]: Yup.array().min(
      1,
      commonValidationMessages.requiredValidation,
    ),
    [CreateMessageKey.messageTitle]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CreateMessageKey.messageDescription]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CreateMessageKey.persistence]: Yup.boolean(),
    [CreateMessageKey.addLinkInformation]: Yup.boolean(),
    [CreateMessageKey.linkName]: Yup.string().when(
      CreateMessageKey.addLinkInformation,
      {
        is: true, // only validate when true
        then: (schema) =>
          schema
            .required(commonValidationMessages.requiredValidation)
            .matches(
              INPUT_LIMIT.ALPHA_NUMERIC_WITH_SOME_SPECIAL_CHAR,
              commonValidationMessages.onlyAlphanumericSpacesAllowed,
            ),
        otherwise: (schema) => schema.optional(), // not required
      },
    ),
    [CreateMessageKey.linkUrl]: Yup.string().when(
      CreateMessageKey.addLinkInformation,
      {
        is: true, // only validate when true
        then: (schema) =>
          schema
            .required(commonValidationMessages.requiredValidation)
            .matches(
              INPUT_LIMIT.LINK_URL,
              commonValidationMessages.invalidLinkURL,
            ),
        otherwise: (schema) => schema.optional(), // not required
      },
    ),
    [CreateMessageKey.linkPosition]: Yup.string().default(""),
    [CreateMessageKey.timeZone]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),

    [CreateMessageKey.fromDate]: Yup.string().when(CreateMessageKey.timeZone, {
      is: (val: string | undefined) => !!val, // only required if timeZone has a value
      then: (schema) =>
        schema
          .required(commonValidationMessages.requiredValidation)
          .test(
            "is-future-date",
            commonValidationMessages.enterValidFromDateTime,
            function (value) {
              const { timeZone } = this.parent;
              if (!value || !timeZone) return true;

              const parsed = parseDateAndTime(
                value,
                userCountryDateFormatWithTime,
              );
              // Now convert to selected timezone using parsed date's format
              let fromDateInTargetTimezone;

              if (isDateAlreadyConvertedWithTimezone(value)) {
                fromDateInTargetTimezone = dayjs.tz(
                  value,
                  userCountryDateFormatWithTime,
                  timeZone,
                );
              } else {
                fromDateInTargetTimezone = dayjs(parsed).tz(timeZone);
              }

              if (!fromDateInTargetTimezone.isValid()) {
                return this.createError({
                  message: commonValidationMessages.enterValidFromDateTime,
                });
              }

              // Get current time in the selected timezone
              const currentTimeInTimezone = dayjs().tz(timeZone);

              // Compare dates (both are now in the same timezone)
              return fromDateInTargetTimezone.isAfter(currentTimeInTimezone);
            },
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    [CreateMessageKey.toDate]: Yup.string().when(CreateMessageKey.timeZone, {
      is: (val: string | undefined) => !!val,
      then: (schema) =>
        schema
          .required(commonValidationMessages.requiredValidation)
          .test(
            "is-greater",
            commonValidationMessages.toDateTimeFromDateValidationMessage,
            function (value) {
              const { fromDate, timeZone } = this.parent;
              if (!fromDate || !value) return true;

              // Parse dates
              const fromDateParsed = parseDateAndTime(
                fromDate,
                userCountryDateFormatWithTime,
                timeZone,
              );
              const toDateParsed = parseDateAndTime(
                value,
                userCountryDateFormatWithTime,
                timeZone,
              );
              // Check if dates are valid
              if (!fromDateParsed?.isValid() || !toDateParsed?.isValid()) {
                // Fallback to native Date parsing if dayjs parsing fails
                return new Date(value) >= new Date(fromDate);
              }

              // If timezone is provided, treat both dates as being in that timezone
              if (timeZone) {
                const fromDateInTimezone = dayjs.tz(
                  fromDateParsed.format(DATE_FORMAT.YYYY_MM_DD_HH_MM_SS_DASH),
                  DATE_FORMAT.YYYY_MM_DD_HH_MM_SS_DASH,
                  timeZone,
                );
                const toDateInTimezone = dayjs.tz(
                  toDateParsed.format(DATE_FORMAT.YYYY_MM_DD_HH_MM_SS_DASH),
                  DATE_FORMAT.YYYY_MM_DD_HH_MM_SS_DASH,
                  timeZone,
                );

                // Add 1 minute to fromDate for minimum requirement
                const minimumToDate = fromDateInTimezone.add(1, "minute");

                // toDate should be at least 1 minute greater than fromDate
                return (
                  toDateInTimezone.isAfter(minimumToDate) ||
                  toDateInTimezone.isSame(minimumToDate)
                );
              }

              // Compare parsed dates without timezone conversion
              // toDate should be at least 1 minute greater than fromDate
              return (
                toDateParsed.isAfter(fromDateParsed.add(1, "minute")) ||
                toDateParsed.isSame(fromDateParsed.add(1, "minute"))
              );
            },
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
